import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import PlayerFinances from './PlayerFinances';

const BACKEND_URL = process.env.REACT_APP_API_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(59, 130, 246, 0.2);
    border-radius: 10px;
    transition: background 0.2s ease;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(59, 130, 246, 0.4);
  }
`;

interface FinancialComponentProps {
  leagueId: number;
  staffRoles?: number[];
  onBack: () => void;
}

interface PayoutSetting {
  name: string;
  description: string;
  payouts: {
    placement: number;
    amount: number;
    type: string;
    item_desc?: string;
  }[];
}

interface LeaderboardPayout {
  leaderboard_name: string;
  placement: number;
  amount: number;
  type: 'money' | 'item';
  item_desc?: string;
  winner?: any; // Will contain winner data (bowler or team)
}

interface Award {
  id: number;
  name: string;
  type: 'money' | 'item';
  amount: number;
  item_desc?: string;
  per_date: boolean;
  overall: boolean;
  squad_name?: string;
  squad_date?: string;
}

interface LeaderboardPlacements {
  mostPoints: Array<{
    team_id: number;
    team_name: string;
    total_points: number;
  }>;
  overallPinfall: Array<{
    bowler_id: number;
    bowler_name: string;
    total_pinfall: number;
    games_played: number;
    average: number;
  }>;
  overallTeamPinfall: Array<{
    team_id: number;
    team_name: string;
    total_pinfall: number;
    games_played: number;
    average: number;
  }>;
  // Add support for division-specific leaderboards
  divisionPinfall: {
    [divisionDescription: string]: Array<{
      bowler_id: number;
      bowler_name: string;
      total_pinfall: number;
      games_played: number;
      average: number;
      division?: string;
    }>;
  };
}

interface PaymentHistory {
  id: number;
  payout_date: string;
  amount: number;
  type?: string;
  item_desc?: string;
  placement?: number;
  recipient_type: 'team' | 'bowler';
  recipient_name: string;
  category: 'Leaderboard' | 'Award' | 'Partial' | 'Side Event';
  award_name?: string;
  reason?: string;
  checkmark?: number;
  payout_source?: string;
}

interface LeagueSummary {
  admin_name: string;
  league_name: string;
  league_pot: number;
  payouts_made: number;
  balance: number;
}

interface TeamWithMembers {
  team_id: number;
  team_name: string;
  members: Array<{
    bowler_id: number;
    bowler_name: string;
    player_id: number;
    registration_status: 'pending' | 'active' | 'completed' | 'canceled';
  }>;
}

interface PayoutError {
  type: 'status_error';
  message: string;
  ineligible_members: Array<{
    bowler_name: string;
    registration_status: string;
  }>;
}

interface PayoutReceipt {
  success: boolean;
  recipient_type: 'bowler' | 'team';
  amount: number;
  amount_per_member?: number;
  members_count?: number;
  award_name: string;
  payout_date: string;
  recipient_name?: string;
}

const FinancialComponent: React.FC<FinancialComponentProps> = ({ leagueId, staffRoles, onBack }) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<'main' | 'playerFinances'>('main');
  const [payoutSettings, setPayoutSettings] = useState<PayoutSetting[]>([]);
  const [awards, setAwards] = useState<Award[]>([]);
  const [leaderboardPlacements, setLeaderboardPlacements] = useState<LeaderboardPlacements | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [leagueSummary, setLeagueSummary] = useState<LeagueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddAwardModal, setShowAddAwardModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showLeaderboardPayoutModal, setShowLeaderboardPayoutModal] = useState(false);
  const [selectedAward, setSelectedAward] = useState<Award | null>(null);
  const [selectedLeaderboardPayout, setSelectedLeaderboardPayout] = useState<LeaderboardPayout | null>(null);
  const [teamsWithMembers, setTeamsWithMembers] = useState<TeamWithMembers[]>([]);
  const [payoutReceipt, setPayoutReceipt] = useState<PayoutReceipt | null>(null);
  const [payoutError, setPayoutError] = useState<PayoutError | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showManageLeaderboardsModal, setShowManageLeaderboardsModal] = useState(false);
  const [showManageAwardsModal, setShowManageAwardsModal] = useState(false);
  const [showAddLeaderboardModal, setShowAddLeaderboardModal] = useState(false);
  const [showAddLeaderboardPayoutModal, setShowAddLeaderboardPayoutModal] = useState(false);
  const [selectedLeaderboardForManagement, setSelectedLeaderboardForManagement] = useState<PayoutSetting | null>(null);
  const [newLeaderboard, setNewLeaderboard] = useState({
    name: 'Most Points',
    description: 'Team with the most points'
  });
  const [selectedDivision, setSelectedDivision] = useState<string>('overall');
  const [newLeaderboardPayout, setNewLeaderboardPayout] = useState({
    placement: 1,
    type: 'money' as 'money' | 'item',
    amount: 0,
    item_desc: ''
  });
  const [newAward, setNewAward] = useState({
    name: '',
    type: 'money' as 'money' | 'item',
    amount: 0,
    item_desc: '',
    per_date: false,
    overall: false
  });

  const _lastName = (n: string) => (n || '').trim().split(/\s+/).pop()?.toLowerCase() ?? '';
  const _fmtName = (n: string) => { const p = (n || '').trim().split(/\s+/); if (p.length < 2) return n; const last = p.pop()!; return `${last}, ${p.join(' ')}`; };

  const handleCheckmark = useCallback(async (paymentId: number, current: number | undefined) => {
    const newVal = current ? 0 : 1;
    setPaymentHistory(prev => prev.map(p => p.id === paymentId ? { ...p, checkmark: newVal } : p));
    try {
      await axios.put(`${BACKEND_URL}/admin/payouts/${paymentId}/checkmark`, { checkmark: newVal }, { headers: authHeader() });
    } catch {
      setPaymentHistory(prev => prev.map(p => p.id === paymentId ? { ...p, checkmark: current } : p));
    }
  }, []);

  const groupedPaymentHistory = (() => {
    const map = new Map<string, PaymentHistory[]>();
    for (const p of paymentHistory) {
      const key = p.recipient_name || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return Array.from(map.entries())
      .map(([name, rows]) => ({ name, rows, total: rows.reduce((s, r) => s + Number(r.amount), 0) }))
      .sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)));
  })();

  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token'); // Use 'token' instead of 'authToken'
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

        // Fetch payout settings (use leaderboard endpoint)
        const payoutResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        let payoutData: PayoutSetting[] = [];
        if (payoutResponse.ok) {
          payoutData = await payoutResponse.json();
          setPayoutSettings(payoutData);
        } else {
          console.error('Failed to fetch payout settings:', payoutResponse.status);
        }

        // Fetch leaderboard data for placements
        const pointsResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/most-points`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const pinfallResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/overall-pinfall`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const teamPinfallResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/overall-team-pinfall`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (pointsResponse.ok && pinfallResponse.ok && teamPinfallResponse.ok) {
          const pointsData = await pointsResponse.json();
          const pinfallData = await pinfallResponse.json();
          const teamPinfallData = await teamPinfallResponse.json();
          
          // Check for division-specific Overall Pinfall leaderboards
          const divisionLeaderboards = payoutData.filter((setting: PayoutSetting) => 
            setting.name === 'Overall Pinfall' && 
            setting.description && 
            setting.description.startsWith('Division ')
          );
          
          // Fetch data for each division leaderboard
          const divisionData: { [divisionDescription: string]: any[] } = {};
          for (const divisionLeaderboard of divisionLeaderboards) {
            const division = divisionLeaderboard.description.replace('Division ', '').trim().toLowerCase();
            
            const divisionResponse = await fetch(
              `${apiUrl}/admin/leaderboard/leagues/${leagueId}/overall-pinfall?division=${division}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (divisionResponse.ok) {
              const data = await divisionResponse.json();
              divisionData[divisionLeaderboard.description] = data;
            }
          }
          
          setLeaderboardPlacements({
            mostPoints: pointsData,
            overallPinfall: pinfallData,
            overallTeamPinfall: teamPinfallData,
            divisionPinfall: divisionData
          });
        }

        // Fetch awards
        const awardsResponse = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/awards`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (awardsResponse.ok) {
          const awardsData = await awardsResponse.json();
          setAwards(awardsData);
        }

        // Fetch payment history
        const historyResponse = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/payment-history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setPaymentHistory(historyData);
        }

        // Fetch league summary
        const summaryResponse = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setLeagueSummary(summaryData);
        }

      } catch (error) {
        console.error('Error fetching financial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialData();
  }, [leagueId]);

  const getLeaderboardWinner = (leaderboardName: string, placement: number) => {
    if (!leaderboardPlacements) return null;

    // Make matching more flexible by checking for partial matches
    const lowerLeaderboardName = leaderboardName.toLowerCase();
    
    if (lowerLeaderboardName.includes('most points') || lowerLeaderboardName.includes('points')) {
      // Get team with highest points - they're already sorted by points DESC
      const winner = leaderboardPlacements.mostPoints[placement - 1];
      return winner;
    } else if (lowerLeaderboardName.includes('overall pinfall') && lowerLeaderboardName.includes('team')) {
      // Get team with highest pinfall - they're already sorted by pinfall DESC
      const winner = leaderboardPlacements.overallTeamPinfall[placement - 1];
      return winner;
    } else if (lowerLeaderboardName.includes('overall pinfall') || lowerLeaderboardName.includes('pinfall')) {
      // Get bowler with highest pinfall - they're already sorted by pinfall DESC
      const winner = leaderboardPlacements.overallPinfall[placement - 1];
      return winner;
    } else {
      return null;
    }
  };

  const getLeaderboardWinnerWithDescription = (leaderboardName: string, description: string, placement: number) => {
    if (!leaderboardPlacements) return null;

    // Check if this is a division-specific Overall Pinfall leaderboard
    if (leaderboardName === 'Overall Pinfall' && description && description.startsWith('Division ')) {
      const divisionData = leaderboardPlacements.divisionPinfall[description];
      if (divisionData && divisionData.length > 0) {
        return divisionData[placement - 1];
      }
      return null;
    }
    
    // Fall back to the original logic for non-division leaderboards
    return getLeaderboardWinner(leaderboardName, placement);
  };

  const handleAddAward = async () => {
    if (!newAward.name) {
      alert('Award name is required');
      return;
    }

    if (newAward.type === 'money' && (!newAward.amount || newAward.amount <= 0)) {
      alert('Amount must be greater than 0 for money awards');
      return;
    }

    if (newAward.type === 'item' && !newAward.item_desc) {
      alert('Item description is required for item awards');
      return;
    }

    if (!newAward.per_date && !newAward.overall) {
      alert('Award must be either per-date or overall (or both)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const awardToAdd = {
        ...newAward,
        amount: newAward.type === 'item' ? undefined : newAward.amount,
        item_desc: newAward.type === 'money' ? undefined : newAward.item_desc
      };

      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/awards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(awardToAdd)
      });

      if (response.ok) {
        const newAwardData = await response.json();
        setAwards([...awards, newAwardData]);
        setNewAward({
          name: '',
          type: 'money',
          amount: 0,
          item_desc: '',
          per_date: false,
          overall: false
        });
        setShowAddAwardModal(false);
      } else {
        alert('Failed to add award');
      }
    } catch (error) {
      console.error('Error adding award:', error);
      alert('Failed to add award');
    }
  };

  const handleAddLeaderboardPayout = async () => {
    if (!newLeaderboard.name || !newLeaderboard.description) {
      alert('Leaderboard name and description are required');
      return;
    }

    if (newLeaderboardPayout.type === 'money' && (!newLeaderboardPayout.amount || newLeaderboardPayout.amount <= 0)) {
      alert('Amount must be greater than 0 for money payouts');
      return;
    }

    if (newLeaderboardPayout.type === 'item' && !newLeaderboardPayout.item_desc) {
      alert('Item description is required for item payouts');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newLeaderboard.name,
          description: newLeaderboard.description,
          placement: newLeaderboardPayout.placement,
          type: newLeaderboardPayout.type,
          amount: newLeaderboardPayout.type === 'money' ? newLeaderboardPayout.amount : 0,
          item_desc: newLeaderboardPayout.type === 'item' ? newLeaderboardPayout.item_desc : null
        })
      });

      if (response.ok) {
        // Refresh payout settings
        const payoutResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (payoutResponse.ok) {
          const payoutData = await payoutResponse.json();
          setPayoutSettings(payoutData);
        }

        setNewLeaderboard({ name: 'Most Points', description: 'Team with the most points' });
        setNewLeaderboardPayout({
          placement: 1,
          type: 'money',
          amount: 0,
          item_desc: ''
        });
        setShowAddLeaderboardModal(false);
      } else {
        alert('Failed to add payout setting');
      }
    } catch (error) {
      console.error('Error adding payout setting:', error);
      alert('Failed to add payout setting');
    }
  };

  const handleDeleteLeaderboardPayout = async (payoutSetting: PayoutSetting, placement: number) => {
    if (!window.confirm(`Delete ${placement}${placement === 1 ? 'st' : placement === 2 ? 'nd' : placement === 3 ? 'rd' : 'th'} place payout?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: payoutSetting.name,
          description: payoutSetting.description,
          placement: placement
        })
      });

      if (response.ok) {
        // Refresh payout settings
        const payoutResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (payoutResponse.ok) {
          const payoutData = await payoutResponse.json();
          setPayoutSettings(payoutData);
        }
      } else {
        alert('Failed to delete payout setting');
      }
    } catch (error) {
      console.error('Error deleting payout setting:', error);
      alert('Failed to delete payout setting');
    }
  };

  const handleDeleteAward = async (awardId: number) => {
    if (!window.confirm('Delete this award? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/awards/${awardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Remove from local state
        setAwards(awards.filter(award => award.id !== awardId));
      } else {
        alert('Failed to delete award');
      }
    } catch (error) {
      console.error('Error deleting award:', error);
      alert('Failed to delete award');
    }
  };

  const handleProcessAwardPayout = async (award: Award) => {
    setSelectedAward(award);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Fetch teams with members
      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/teams-with-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const teamsData = await response.json();
        setTeamsWithMembers(teamsData);
        setShowPayoutModal(true);
      } else {
        alert('Failed to fetch teams data');
      }
    } catch (error) {
      console.error('Error fetching teams data:', error);
      alert('Failed to fetch teams data');
    }
  };

  const handleProcessLeaderboardPayout = async (leaderboardName: string, description: string, payout: any, winner: any) => {
    const leaderboardPayout: LeaderboardPayout = {
      leaderboard_name: leaderboardName === 'Overall Pinfall' && description && description.startsWith('Division ')
        ? `${leaderboardName} - ${description}`
        : leaderboardName,
      placement: payout.placement,
      amount: payout.amount,
      type: payout.type,
      item_desc: payout.item_desc,
      winner: winner
    };
    
    setSelectedLeaderboardPayout(leaderboardPayout);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Fetch teams with members
      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/teams-with-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const teamsData = await response.json();
        setTeamsWithMembers(teamsData);
        setShowLeaderboardPayoutModal(true);
      } else {
        alert('Failed to fetch teams data');
      }
    } catch (error) {
      console.error('Error fetching teams data:', error);
      alert('Failed to fetch teams data');
    }
  };

  const handleBowlerPayout = async (bowler: any) => {
    if (!selectedAward) return;

    // Check if bowler has eligible registration status
    if (!bowler.registration_status || (bowler.registration_status !== 'active' && bowler.registration_status !== 'completed')) {
      setPayoutError({
        type: 'status_error',
        message: 'Cannot process payout for members with incomplete registration',
        ineligible_members: [{
          bowler_name: bowler.bowler_name,
          registration_status: bowler.registration_status || 'unknown'
        }]
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // For money awards use the amount, for item awards use 0
      const payoutAmount = selectedAward.type === 'money' ? selectedAward.amount : 0;

      const requestBody = {
        bowler_id: bowler.bowler_id,
        player_id: bowler.player_id,
        amount: payoutAmount
      };

      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/awards/${selectedAward.id}/payout-bowler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
       
        
        setPayoutReceipt({
          ...result,
          award_name: selectedAward.name,
          payout_date: new Date().toISOString(),
          amount: Number(result.amount) || 0, // Ensure amount is a number
          amount_per_member: result.amount_per_member ? Number(result.amount_per_member) : undefined
        });
        
        // Refresh financial data in background without page reload
        const refreshToken = localStorage.getItem('token');
        const refreshApiUrl = BACKEND_URL || 'http://localhost:5000/api';
        
        // Refresh payment history
        const historyResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/payment-history`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setPaymentHistory(historyData);
        }
        
        // Refresh league summary
        const summaryResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/summary`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setLeagueSummary(summaryData);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to process payout: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing bowler payout:', error);
      alert('Failed to process payout');
    }
  };

  const handleTeamPayout = async (team: TeamWithMembers) => {
    if (!selectedAward) return;

    // Check if all team members have eligible registration status
    const ineligibleMembers = team.members.filter(member => 
      !member.registration_status || (member.registration_status !== 'active' && member.registration_status !== 'completed')
    );

    if (ineligibleMembers.length > 0) {
      setPayoutError({
        type: 'status_error',
        message: `Cannot process team payout. ${ineligibleMembers.length} member(s) have incomplete registration.`,
        ineligible_members: ineligibleMembers.map(member => ({
          bowler_name: member.bowler_name,
          registration_status: member.registration_status || 'unknown'
        }))
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // For money awards use the amount, for item awards use 0
      const payoutAmount = selectedAward.type === 'money' ? selectedAward.amount : 0;

      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/awards/${selectedAward.id}/payout-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          team_id: team.team_id,
          members: team.members,
          total_amount: payoutAmount
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        
        setPayoutReceipt({
          ...result,
          award_name: selectedAward.name,
          payout_date: new Date().toISOString(),
          amount: Number(result.amount) || 0, // Ensure amount is a number
          amount_per_member: result.amount_per_member ? Number(result.amount_per_member) : undefined
        });
        
        // Refresh financial data in background without page reload
        const refreshToken = localStorage.getItem('token');
        const refreshApiUrl = BACKEND_URL || 'http://localhost:5000/api';
        
        // Refresh payment history
        const historyResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/payment-history`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setPaymentHistory(historyData);
        }
        
        // Refresh league summary
        const summaryResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/summary`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setLeagueSummary(summaryData);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to process team payout: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing team payout:', error);
      alert('Failed to process team payout');
    }
  };

  const handleLeaderboardBowlerPayout = async (bowler: any) => {
    if (!selectedLeaderboardPayout) return;

    // Check if bowler has completed registration status (stricter than awards)
    if (bowler.registration_status !== 'completed') {
      setPayoutError({
        type: 'status_error',
        message: 'Cannot process leaderboard payout for members without completed registration',
        ineligible_members: [{
          bowler_name: bowler.bowler_name,
          registration_status: bowler.registration_status || 'unknown'
        }]
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const requestBody = {
        leaderboard_name: selectedLeaderboardPayout.leaderboard_name,
        placement: selectedLeaderboardPayout.placement,
        recipient_type: 'bowler',
        recipient_id: bowler.bowler_id,
        player_id: bowler.player_id,
        amount: selectedLeaderboardPayout.amount,
        type: selectedLeaderboardPayout.type,
        item_desc: selectedLeaderboardPayout.item_desc
      };

      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/leaderboard-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        setPayoutReceipt({
          ...result,
          award_name: `${selectedLeaderboardPayout.leaderboard_name} - ${selectedLeaderboardPayout.placement}${selectedLeaderboardPayout.placement === 1 ? 'st' : selectedLeaderboardPayout.placement === 2 ? 'nd' : selectedLeaderboardPayout.placement === 3 ? 'rd' : 'th'} Place`,
          payout_date: new Date().toISOString(),
          amount: Number(result.amount) || 0,
          recipient_name: bowler.bowler_name
        });
        
        // Refresh financial data in background
        const refreshToken = localStorage.getItem('token');
        const refreshApiUrl = BACKEND_URL || 'http://localhost:5000/api';
        
        const historyResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/payment-history`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setPaymentHistory(historyData);
        }
        
        const summaryResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/summary`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setLeagueSummary(summaryData);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to process leaderboard payout: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing leaderboard bowler payout:', error);
      alert('Failed to process leaderboard payout');
    }
  };

  const handleLeaderboardTeamPayout = async (team: TeamWithMembers) => {
    if (!selectedLeaderboardPayout) return;

    // Check if all team members have completed registration status
    const ineligibleMembers = team.members.filter(member => 
      member.registration_status !== 'completed'
    );

    if (ineligibleMembers.length > 0) {
      setPayoutError({
        type: 'status_error',
        message: `Cannot process leaderboard team payout. ${ineligibleMembers.length} member(s) do not have completed registration.`,
        ineligible_members: ineligibleMembers.map(member => ({
          bowler_name: member.bowler_name,
          registration_status: member.registration_status || 'unknown'
        }))
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const requestBody = {
        leaderboard_name: selectedLeaderboardPayout.leaderboard_name,
        placement: selectedLeaderboardPayout.placement,
        recipient_type: 'team',
        recipient_id: team.team_id,
        members: team.members,
        amount: selectedLeaderboardPayout.amount,
        type: selectedLeaderboardPayout.type,
        item_desc: selectedLeaderboardPayout.item_desc
      };

      const response = await fetch(`${apiUrl}/admin/financial/leagues/${leagueId}/leaderboard-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        setPayoutReceipt({
          ...result,
          award_name: `${selectedLeaderboardPayout.leaderboard_name} - ${selectedLeaderboardPayout.placement}${selectedLeaderboardPayout.placement === 1 ? 'st' : selectedLeaderboardPayout.placement === 2 ? 'nd' : selectedLeaderboardPayout.placement === 3 ? 'rd' : 'th'} Place`,
          payout_date: new Date().toISOString(),
          amount: Number(result.amount) || 0,
          amount_per_member: result.amount_per_member ? Number(result.amount_per_member) : undefined,
          recipient_name: team.team_name
        });
        
        // Refresh financial data in background
        const refreshToken = localStorage.getItem('token');
        const refreshApiUrl = BACKEND_URL || 'http://localhost:5000/api';
        
        const historyResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/payment-history`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setPaymentHistory(historyData);
        }
        
        const summaryResponse = await fetch(`${refreshApiUrl}/admin/financial/leagues/${leagueId}/summary`, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setLeagueSummary(summaryData);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to process leaderboard team payout: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing leaderboard team payout:', error);
      alert('Failed to process leaderboard team payout');
    }
  };

  const closePayoutModal = () => {
    setShowPayoutModal(false);
    setShowLeaderboardPayoutModal(false);
    setSelectedAward(null);
    setSelectedLeaderboardPayout(null);
    setTeamsWithMembers([]);
    setPayoutReceipt(null);
    setPayoutError(null);
  };


  if (currentView === 'playerFinances') {
    return <PlayerFinances leagueId={leagueId} onBack={() => setCurrentView('main')} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('financial.title')}</h1>
            </div>
          </div>
        </div>
      </div>

         {/* Help Card */}

      <div
        className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-yellow-300 font-bold">?</span>
      </div>
      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.managingLeagueFinancialHelp')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.managingLeagueFinancialHelpContent')}
            </div>
          
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            <p className="text-white ml-4">{t('financial.loading')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Top Row - Four Cards Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Payout Settings Card */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-xl font-bold text-white">📋 {t('financial.payoutSettings')}</h2>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => setShowAddLeaderboardModal(true)}
                      className="bg-blue-600/40 hover:bg-blue-600/60 text-white p-1.5 rounded-lg transition duration-200 border border-blue-400/50"
                      title={t('financial.addButton')}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setShowManageLeaderboardsModal(true)}
                      className="bg-blue-600/40 hover:bg-blue-600/60 text-white p-1.5 rounded-lg transition duration-200 border border-blue-400/50"
                      title={t('financial.manageButton')}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div 
                  className="max-h-48 overflow-y-auto space-y-3 pr-2 custom-scrollbar"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(59, 130, 246, 0.3) transparent'
                  }}
                >
                  {payoutSettings.map((setting, index) => (
                    <div key={index} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-semibold text-white">
                          {setting.name === 'Overall Pinfall' && setting.description && setting.description.startsWith('Division ')
                            ? `${setting.name} - ${setting.description}`
                            : setting.name}
                        </h3>
                        <span className="text-xs text-blue-200">{setting.payouts.length} {t('financial.pos')}</span>
                      </div>
                      <div className="space-y-1">
                        {setting.payouts.slice(0, 3).map((payout, pIndex) => {
                          return (
                            <div key={pIndex} className="flex justify-between items-center text-xs">
                              <span className="text-white">
                                {payout.placement === 1 ? t('financial.ordinals.1st') : 
                                 payout.placement === 2 ? t('financial.ordinals.2nd') : 
                                 payout.placement === 3 ? t('financial.ordinals.3rd') : 
                                 t('financial.ordinals.nth', { n: payout.placement })}
                              </span>
                              <span className="text-green-300">
                                {payout.type === 'money' ? `$${payout.amount}` : payout.item_desc}
                              </span>
                            </div>
                          );
                        })}
                        {setting.payouts.length > 3 && (
                          <div className="text-xs text-blue-200 text-center">
                            +{setting.payouts.length - 3} {t('financial.more')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Awards Card */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-xl font-bold text-white">🏆 {t('financial.awards')}</h2>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => setShowAddAwardModal(true)}
                      className="bg-yellow-600/40 hover:bg-yellow-600/60 text-white p-1.5 rounded-lg transition duration-200 border border-yellow-400/50"
                      title={t('financial.addButton')}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setShowManageAwardsModal(true)}
                      className="bg-yellow-600/40 hover:bg-yellow-600/60 text-white p-1.5 rounded-lg transition duration-200 border border-yellow-400/50"
                      title={t('financial.manageButton')}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div 
                  className="max-h-48 overflow-y-auto space-y-3 pr-2 custom-scrollbar"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(59, 130, 246, 0.3) transparent'
                  }}
                >
                  {awards.map((award) => (
                    <div key={award.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-semibold text-white">{award.name}</h3>
                        <span className="text-xs text-blue-200">{award.type}</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white">{t('financial.value')}</span>
                          <span className="text-green-300">
                            {award.type === 'money' ? `$${award.amount}` : award.item_desc}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white">{t('financial.frequency')}</span>
                          <span className="text-blue-200">
                            {award.per_date ? t('financial.perDate') : t('financial.oneTime')}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-white">{t('financial.scope')}</span>
                          <span className="text-purple-200">
                            {award.overall ? t('financial.overall') : t('financial.squad')}
                          </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleProcessAwardPayout(award)}
                        className="mt-2 w-full bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-200 px-2 py-1 rounded text-xs transition duration-200 border border-yellow-400/30">
                        {t('financial.processPayout')} 💵
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* League Summary Card */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <h2 className="text-xl font-bold text-white mb-3">📊 {t('financial.leagueSummary')}</h2>
                {leagueSummary ? (
                  <div className="space-y-4">
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/80">{t('financial.admin')}:</span>
                          <span className="text-white font-medium">{leagueSummary.admin_name}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/80">{t('financial.leaguePot')}:</span>
                          <span className="text-green-300 font-medium">${leagueSummary.league_pot.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/80">{t('financial.payoutsMade')}:</span>
                          <span className="text-red-300 font-medium">${leagueSummary.payouts_made.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-white/20 pt-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-white font-medium">{t('financial.balance')}:</span>
                            <span className={`font-bold ${leagueSummary.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              ${leagueSummary.balance.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>

              {/* Finances by player */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <h2 className="text-xl font-bold text-white mb-3">👥 {t('financial.payoutsByPlayer')}</h2>
                 <button
                  onClick={() => setCurrentView('playerFinances')}
                  className="bg-green-600/40 hover:bg-green-700/40 text-white font-bold py-3 px-6 rounded-lg transition duration-200 border border-green-400/50"
                >
                  {t('financial.viewPlayerBalances')} →
                </button>
              </div>
            </div>

            {/* Leaderboard Winners Section */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-4">🏅 {t('financial.currentLeaderboardWinners')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {payoutSettings.map((setting, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      {setting.name === 'Overall Pinfall' && setting.description && setting.description.startsWith('Division ')
                        ? `${setting.name} - ${setting.description}`
                        : setting.name}
                    </h3>
                    <div className="space-y-2">
                      {setting.payouts.slice(0, 5).map((payout, pIndex) => {
                        const winner = getLeaderboardWinnerWithDescription(setting.name, setting.description, payout.placement);
                        return (
                          <div key={pIndex} className="flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                              <span className="text-yellow-300 font-medium text-sm">
                                {payout.placement === 1 ? '🥇' : 
                                 payout.placement === 2 ? '🥈' : 
                                 payout.placement === 3 ? '🥉' : 
                                 `${payout.placement}th`}
                              </span>
                              <span className="text-white text-sm">
                                {winner ? (
                                  // Show bowler_name for Overall Pinfall (including divisions), team_name for everything else
                                  setting.name.toLowerCase().includes('overall pinfall') && !setting.name.toLowerCase().includes('team')
                                    ? (winner as any).bowler_name
                                    : (winner as any).team_name
                                ) : t('financial.tbd')}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-green-300 text-sm">
                                {payout.type === 'money' ? `$${payout.amount}` : payout.item_desc}
                              </div>
                              <button 
                                onClick={() => handleProcessLeaderboardPayout(setting.name, setting.description, payout, winner)}
                                disabled={!winner}
                                className={`mt-1 px-2 py-1 rounded text-xs transition duration-200 border ${
                                  winner 
                                    ? 'bg-yellow-600/50 hover:bg-yellow-700/50 text-white border-yellow-400/50' 
                                    : 'bg-gray-600/30 text-gray-400 border-gray-500/30 cursor-not-allowed'
                                }`}
                              >
                                {t('financial.payout')} 💵
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment History Section */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">💸 {t('financial.payoutHistory')}</h2>
                <span className="text-blue-200">{paymentHistory.length} {t('financial.transactions')}</span>
              </div>
              <div 
                className="max-h-80 overflow-y-auto overflow-x-auto custom-scrollbar"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(59, 130, 246, 0.3) transparent'
                }}
              >
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-blue/20 backdrop-blur-sm">
                    <tr className="text-left border-b border-white/20">
                      <th className="text-white font-semibold pb-2 pt-2">{t('financial.recipient')}</th>
                      <th className="text-white font-semibold pb-2 pt-2">{t('financial.date')}</th>
                      <th className="text-white font-semibold pb-2 pt-2">{t('financial.category')}</th>
                      <th className="text-white font-semibold pb-2 pt-2">{t('financial.amount')}</th>
                      <th className="text-white font-semibold pb-2 pt-2">{t('financial.details')}</th>
                      <th className="text-white font-semibold pb-2 pt-2 text-center">✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedPaymentHistory.map((group, gi) => {
                      const multi = group.rows.length > 1;
                      return (
                        <React.Fragment key={`grp-${group.name}-${gi}`}>
                          {group.rows.map((payment, ri) => (
                            <tr key={payment.id} className={`border-b border-white/10 ${multi ? 'bg-white/5' : ''} ${ri === 0 && gi > 0 ? 'border-t-2 border-white/20' : ''}`}>
                              <td className="py-2 text-white">
                                {ri === 0 ? (
                                  <div>
                                    <div className="font-medium">{_fmtName(group.name)}</div>
                                    <div className="text-xs text-blue-200">{payment.recipient_type}</div>
                                  </div>
                                ) : (
                                  <span className="text-white/30 pl-3 text-xs">└─</span>
                                )}
                              </td>
                              <td className="py-2 text-blue-200">
                                {new Date(payment.payout_date).toLocaleDateString()}
                              </td>
                              <td className="py-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  payment.category === 'Leaderboard' ? 'bg-blue-500/20 text-blue-300' :
                                  payment.category === 'Award' ? 'bg-yellow-500/20 text-yellow-300' :
                                  payment.category === 'Side Event' ? 'bg-purple-500/20 text-purple-300' :
                                  'bg-gray-500/20 text-gray-300'
                                }`}>
                                  {payment.category}
                                </span>
                              </td>
                              <td className="py-2 text-green-300">
                                {payment.type === 'money' || payment.category === 'Leaderboard' || payment.category === 'Side Event'
                                  ? `$${Number(payment.amount).toFixed(2)}`
                                  : (payment.item_desc || `$${Number(payment.amount).toFixed(2)}`)}
                              </td>
                              <td className="py-2 text-blue-200 text-xs">
                                {payment.award_name || payment.reason || (payment.placement ? `${t('financial.position')} ${payment.placement}` : '—')}
                              </td>
                              <td className="py-2 text-center">
                                {payment.payout_source === 'side_event' ? (
                                  <input
                                    type="checkbox"
                                    checked={!!payment.checkmark}
                                    onChange={() => handleCheckmark(payment.id, payment.checkmark)}
                                    className="w-4 h-4 accent-green-500 cursor-pointer"
                                  />
                                ) : (
                                  <span className="text-white/20">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {multi && (
                            <tr className="border-t border-emerald-700/30 bg-emerald-900/20">
                              <td colSpan={3} className="py-1 text-right text-xs font-semibold text-emerald-300 pr-2">
                                Subtotal — {_fmtName(group.name)}
                              </td>
                              <td className="py-1 text-right font-bold text-emerald-400 text-xs pr-2">
                                ${group.total.toFixed(2)}
                              </td>
                              <td colSpan={2} />
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Award Payout Modal */}
      {showPayoutModal && selectedAward && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            {payoutError ? (
              /* Error View */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">❌ {t('financial.awardPayoutModal.errorTitle')}</h3>
                  <button
                    onClick={closePayoutModal}
                    className="text-white/70 hover:text-white transition duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-6 text-center">
                  <div className="text-red-400 text-6xl mb-4">⚠️</div>
                  <h4 className="text-2xl font-bold text-white mb-4">{t('financial.awardPayoutModal.cannotProcess')}</h4>
                  
                  <div className="space-y-4 text-left max-w-md mx-auto">
                    <div className="text-white/90 text-center mb-4">
                      {payoutError.message}
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <h5 className="text-white font-semibold mb-3">{t('financial.awardPayoutModal.incompleteRegistration')}</h5>
                      <div className="space-y-2">
                        {payoutError.ineligible_members.map((member, index) => (
                          <div key={index} className="flex justify-between items-center bg-red-500/10 rounded p-2">
                            <span className="text-white text-sm">{member.bowler_name}</span>
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              member.registration_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                              member.registration_status === 'canceled' ? 'bg-red-500/20 text-red-300' :
                              'bg-gray-500/20 text-gray-300'
                            }`}>
                              {member.registration_status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="text-sm text-white/70 text-center mt-4">
                      {t('financial.awardPayoutModal.eligibilityNote')}
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={closePayoutModal}
                      className="bg-red-600/50 hover:bg-red-600/70 text-white px-6 py-2 rounded-lg transition duration-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : payoutReceipt ? (
              /* Receipt View */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">🧾 {t('financial.awardPayoutModal.receiptTitle')}</h3>
                  <button
                    onClick={closePayoutModal}
                    className="text-white/70 hover:text-white transition duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-white/10 rounded-lg p-6 text-center">
                  <div className="text-green-400 text-6xl mb-4">✅</div>
                  <h4 className="text-2xl font-bold text-white mb-4">{t('financial.awardPayoutModal.paymentSuccessful')}</h4>
                  
                  <div className="space-y-3 text-left max-w-md mx-auto">
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.awardPayoutModal.award')}:</span>
                      <span className="text-white font-medium">{payoutReceipt.award_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.awardPayoutModal.recipientType')}:</span>
                      <span className="text-white font-medium capitalize">{payoutReceipt.recipient_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.awardPayoutModal.totalAmount')}:</span>
                      <span className="text-green-300 font-bold">${(Number(payoutReceipt.amount) || 0).toFixed(2)}</span>
                    </div>
                    {payoutReceipt.recipient_type === 'team' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-white/80">Members:</span>
                          <span className="text-white font-medium">{payoutReceipt.members_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/80">Per Member:</span>
                          <span className="text-green-300 font-medium">${(Number(payoutReceipt.amount_per_member) || 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/80">Date:</span>
                      <span className="text-white font-medium">
                        {new Date(payoutReceipt.payout_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 space-x-3">
                    <button
                      onClick={() => window.print()}
                      className="bg-blue-600/50 hover:bg-blue-600/70 text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      {t('financial.awardPayoutModal.printReceipt')}
                    </button>
                    <button
                      onClick={closePayoutModal}
                      className="bg-green-600/50 hover:bg-green-600/70 text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      {t('financial.awardPayoutModal.done')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Payout Selection View */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">💰 {t('financial.awardPayoutModal.processTitle')}</h3>
                  <button
                    onClick={closePayoutModal}
                    className="text-white/70 hover:text-white transition duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-white/10 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-bold text-white mb-2">{selectedAward.name}</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">{t('financial.awardPayoutModal.awardAmount')}:</span>
                    <span className="text-green-300 font-bold text-xl">
                      {selectedAward.type === 'money' ? `$${selectedAward.amount}` : selectedAward.item_desc}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="text-lg font-semibold text-white">{t('financial.awardPayoutModal.selectRecipients')}:</h5>
                  
                  {teamsWithMembers.map((team) => (
                    <div key={team.team_id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex justify-between items-center mb-3">
                        <h6 className="text-md font-semibold text-white">{team.team_name}</h6>
                        <button
                          onClick={() => handleTeamPayout(team)}
                          className="bg-green-600/50 hover:bg-green-600/70 text-white px-3 py-1 rounded text-sm transition duration-200"
                          disabled={team.members.length === 0}
                        >
                          {selectedAward.type === 'money' ? 
                            `${t('financial.awardPayoutModal.payTeam')} ($${(selectedAward.amount / Math.max(team.members.length, 1)).toFixed(2)} ${t('financial.awardPayoutModal.each')})` :
                            `${t('financial.awardPayoutModal.payTeam')} (${t('financial.awardPayoutModal.itemAward')})`
                          }
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {team.members.length === 0 ? (
                          <p className="text-white/60 text-sm italic">{t('financial.awardPayoutModal.noActiveMembers')}</p>
                        ) : (
                          team.members.map((member) => {
                            const isEligible = member.registration_status === 'active' || member.registration_status === 'completed';
                            return (
                              <div key={member.bowler_id} className={`flex justify-between items-center rounded p-2 ${
                                isEligible ? 'bg-white/5' : 'bg-red-500/10 border border-red-400/30'
                              }`}>
                                <div className="flex flex-col">
                                  <span className={`text-sm ${isEligible ? 'text-white' : 'text-white/70'}`}>
                                    {member.bowler_name}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded mt-1 w-fit ${
                                    member.registration_status === 'active' ? 'bg-green-500/20 text-green-300' :
                                    member.registration_status === 'completed' ? 'bg-blue-500/20 text-blue-300' :
                                    member.registration_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                                    member.registration_status === 'canceled' ? 'bg-red-500/20 text-red-300' :
                                    'bg-gray-500/20 text-gray-300'
                                  }`}>
                                    {t(`financial.awardPayoutModal.statuses.${member.registration_status || 'unknown'}`)}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleBowlerPayout(member)}
                                  disabled={!isEligible}
                                  className={`px-2 py-1 rounded text-xs transition duration-200 ${
                                    isEligible 
                                      ? 'bg-yellow-600/50 hover:bg-yellow-600/70 text-white' 
                                      : 'bg-gray-600/30 text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  {t('financial.awardPayoutModal.payMember')}
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leaderboard Payout Modal */}
      {showLeaderboardPayoutModal && selectedLeaderboardPayout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            {payoutError ? (
              /* Error View */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">❌ {t('financial.leaderboardPayoutModal.errorTitle')}</h3>
                  <button
                    onClick={closePayoutModal}
                    className="text-white/70 hover:text-white transition duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-6 text-center">
                  <div className="text-red-400 text-6xl mb-4">⚠️</div>
                  <h4 className="text-2xl font-bold text-white mb-4">{t('financial.leaderboardPayoutModal.cannotProcess')}</h4>
                  
                  <div className="space-y-4 text-left max-w-md mx-auto">
                    <div className="text-white/90 text-center mb-4">
                      {payoutError.message}
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4">
                      <h5 className="text-white font-semibold mb-3">{t('financial.leaderboardPayoutModal.incompleteRegistration')}:</h5>
                      <div className="space-y-2">
                        {payoutError.ineligible_members.map((member, index) => (
                          <div key={index} className="flex justify-between items-center bg-red-500/10 rounded p-2">
                            <span className="text-white text-sm">{member.bowler_name}</span>
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              member.registration_status === 'active' ? 'bg-yellow-500/20 text-yellow-300' :
                              member.registration_status === 'pending' ? 'bg-orange-500/20 text-orange-300' :
                              member.registration_status === 'canceled' ? 'bg-red-500/20 text-red-300' :
                              'bg-gray-500/20 text-gray-300'
                            }`}>
                              {member.registration_status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="text-sm text-white/70 text-center mt-4">
                      {t('financial.leaderboardPayoutModal.eligibilityNote')}
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={closePayoutModal}
                      className="bg-red-600/50 hover:bg-red-600/70 text-white px-6 py-2 rounded-lg transition duration-200"
                    >
                      {t('financial.leaderboardPayoutModal.close')}
                    </button>
                  </div>
                </div>
              </div>
            ) : payoutReceipt ? (
              /* Receipt View */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">🧾 {t('financial.leaderboardPayoutModal.receiptTitle')}</h3>
                  <button
                    onClick={closePayoutModal}
                    className="text-white/70 hover:text-white transition duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-white/10 rounded-lg p-6 text-center">
                  <div className="text-green-400 text-6xl mb-4">🏆</div>
                  <h4 className="text-2xl font-bold text-white mb-4">{t('financial.leaderboardPayoutModal.paymentSuccessful')}</h4>
                  
                  <div className="space-y-3 text-left max-w-md mx-auto">
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.leaderboardPayoutModal.leaderboard')}:</span>
                      <span className="text-white font-medium">{payoutReceipt.award_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.leaderboardPayoutModal.recipient')}:</span>
                      <span className="text-white font-medium">{payoutReceipt.recipient_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.leaderboardPayoutModal.type')}:</span>
                      <span className="text-white font-medium capitalize">{payoutReceipt.recipient_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.leaderboardPayoutModal.totalAmount')}:</span>
                      <span className="text-green-300 font-bold">${(Number(payoutReceipt.amount) || 0).toFixed(2)}</span>
                    </div>
                    {payoutReceipt.recipient_type === 'team' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-white/80">{t('financial.leaderboardPayoutModal.members')}:</span>
                          <span className="text-white font-medium">{payoutReceipt.members_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/80">{t('financial.leaderboardPayoutModal.perMember')}:</span>
                          <span className="text-green-300 font-medium">${(Number(payoutReceipt.amount_per_member) || 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/80">{t('financial.leaderboardPayoutModal.date')}:</span>
                      <span className="text-white font-medium">
                        {new Date(payoutReceipt.payout_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 space-x-3">
                    <button
                      onClick={() => window.print()}
                      className="bg-blue-600/50 hover:bg-blue-600/70 text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      {t('financial.leaderboardPayoutModal.printReceipt')}
                    </button>
                    <button
                      onClick={closePayoutModal}
                      className="bg-green-600/50 hover:bg-green-600/70 text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      {t('financial.leaderboardPayoutModal.done')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Payout Selection View */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">🏅 {t('financial.leaderboardPayoutModal.processTitle')}</h3>
                  <button
                    onClick={closePayoutModal}
                    className="text-white/70 hover:text-white transition duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-white/10 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-bold text-white mb-2">
                    {selectedLeaderboardPayout.leaderboard_name} - {selectedLeaderboardPayout.placement}
                    {selectedLeaderboardPayout.placement === 1 ? 'st' : 
                     selectedLeaderboardPayout.placement === 2 ? 'nd' : 
                     selectedLeaderboardPayout.placement === 3 ? 'rd' : 'th'} Place
                  </h4>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">{t('financial.leaderboardPayoutModal.payoutAmount')}:</span>
                    <span className="text-green-300 font-bold text-xl">
                      {selectedLeaderboardPayout.type === 'money' ? `$${selectedLeaderboardPayout.amount}` : selectedLeaderboardPayout.item_desc}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-yellow-300">
                    ⚠️ {t('financial.leaderboardPayoutModal.completedOnlyWarning')}
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="text-lg font-semibold text-white">{t('financial.leaderboardPayoutModal.selectRecipients')}:</h5>
                  
                  {teamsWithMembers.map((team) => {
                    const eligibleMembers = team.members.filter(member => member.registration_status === 'completed');
                    const allMembersEligible = team.members.length > 0 && eligibleMembers.length === team.members.length;
                    
                    return (
                      <div key={team.team_id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="flex justify-between items-center mb-3">
                          <h6 className="text-md font-semibold text-white">{team.team_name}</h6>
                          <button
                            onClick={() => handleLeaderboardTeamPayout(team)}
                            disabled={!allMembersEligible}
                            className={`px-3 py-1 rounded text-sm transition duration-200 ${
                              allMembersEligible
                                ? 'bg-green-600/50 hover:bg-green-600/70 text-white'
                                : 'bg-gray-600/30 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {selectedLeaderboardPayout.type === 'money' ? 
                              `${t('financial.leaderboardPayoutModal.payTeam')} ($${(selectedLeaderboardPayout.amount / Math.max(team.members.length, 1)).toFixed(2)} ${t('financial.leaderboardPayoutModal.each')})` :
                              `${t('financial.leaderboardPayoutModal.payTeam')} (${t('financial.leaderboardPayoutModal.itemAward')})`
                            }
                          </button>
                        </div>
                        
                        <div className="space-y-2">
                          {team.members.length === 0 ? (
                            <p className="text-white/60 text-sm italic">{t('financial.leaderboardPayoutModal.noActiveMembers')}</p>
                          ) : (
                            team.members.map((member) => {
                              const isEligible = member.registration_status === 'completed';
                              return (
                                <div key={member.bowler_id} className={`flex justify-between items-center rounded p-2 ${
                                  isEligible ? 'bg-white/5' : 'bg-red-500/10 border border-red-400/30'
                                }`}>
                                  <div className="flex flex-col">
                                    <span className={`text-sm ${isEligible ? 'text-white' : 'text-white/70'}`}>
                                      {member.bowler_name}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded mt-1 w-fit ${
                                      member.registration_status === 'completed' ? 'bg-green-500/20 text-green-300' :
                                      member.registration_status === 'active' ? 'bg-yellow-500/20 text-yellow-300' :
                                      member.registration_status === 'pending' ? 'bg-orange-500/20 text-orange-300' :
                                      member.registration_status === 'canceled' ? 'bg-red-500/20 text-red-300' :
                                      'bg-gray-500/20 text-gray-300'
                                    }`}>
                                      {t(`financial.leaderboardPayoutModal.statuses.${member.registration_status || 'unknown'}`)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleLeaderboardBowlerPayout(member)}
                                    disabled={!isEligible}
                                    className={`px-2 py-1 rounded text-xs transition duration-200 ${
                                      isEligible 
                                        ? 'bg-yellow-600/50 hover:bg-yellow-600/70 text-white' 
                                        : 'bg-gray-600/30 text-gray-400 cursor-not-allowed'
                                    }`}
                                  >
                                    {t('financial.leaderboardPayoutModal.payMember')}
                                  </button>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Award Modal */}
      {showAddAwardModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">🏆 {t('financial.addAwardModal.title')}</h3>
              <button
                onClick={() => setShowAddAwardModal(false)}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Award Name */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  {t('financial.addAwardModal.awardName')} *
                </label>
                <input
                  type="text"
                  value={newAward.name}
                  onChange={(e) => setNewAward({ ...newAward, name: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="e.g., 300 Game, Strike King"
                />
              </div>

              {/* Award Type and Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    {t('financial.addAwardModal.awardType')} *
                  </label>
                  <select
                    value={newAward.type}
                    onChange={(e) => setNewAward({ ...newAward, type: e.target.value as 'money' | 'item' })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="money" className="bg-gray-800">{t('financial.addAwardModal.money')}</option>
                    <option value="item" className="bg-gray-800">{t('financial.addAwardModal.item')}</option>
                  </select>
                </div>

                {newAward.type === 'money' ? (
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('financial.addAwardModal.amount')} ($) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newAward.amount || 0}
                      onChange={(e) => setNewAward({ ...newAward, amount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('financial.addAwardModal.itemDescription')} *
                    </label>
                    <input
                      type="text"
                      value={newAward.item_desc || ''}
                      onChange={(e) => setNewAward({ ...newAward, item_desc: e.target.value })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="e.g., Trophy, Bowling Ball"
                    />
                  </div>
                )}
              </div>

              {/* Award Frequency */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-3">
                  {t('financial.addAwardModal.whenGiven')} *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newAward.per_date}
                      onChange={(e) => setNewAward({ ...newAward, per_date: e.target.checked })}
                      className="w-4 h-4 text-yellow-600 bg-white/10 border-white/20 rounded focus:ring-yellow-500 focus:ring-2"
                    />
                    <span className="ml-2 text-white/80">{t('financial.addAwardModal.eachWeek')}</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newAward.overall}
                      onChange={(e) => setNewAward({ ...newAward, overall: e.target.checked })}
                      className="w-4 h-4 text-yellow-600 bg-white/10 border-white/20 rounded focus:ring-yellow-500 focus:ring-2"
                    />
                    <span className="ml-2 text-white/80">{t('financial.addAwardModal.overallChampion')}</span>
                  </label>
                </div>
                <p className="text-xs text-white/60 mt-2">
                  {t('financial.addAwardModal.bothOptionsNote')}
                </p>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowAddAwardModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition duration-200 border border-white/20"
                >
                  {t('financial.addAwardModal.cancel')}
                </button>
                <button
                  onClick={handleAddAward}
                  className="flex-1 px-4 py-2 bg-yellow-600/50 hover:bg-yellow-600/70 text-white rounded-lg transition duration-200 border border-yellow-400/50"
                >
                  {t('financial.addAwardModal.addAward')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Leaderboard Payout Settings Modal - Create New */}
      {showAddLeaderboardModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">📊 Add New Leaderboard Payout Settings</h3>
              <button
                onClick={() => {
                  setShowAddLeaderboardModal(false);
                  setNewLeaderboard({ name: 'Most Points', description: 'Team with the most points' });
                  setSelectedDivision('overall');
                  setNewLeaderboardPayout({ placement: 1, type: 'money', amount: 0, item_desc: '' });
                }}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Leaderboard Name and Description */}
            <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
              <h4 className="text-lg font-semibold text-white mb-4">1. Leaderboard Information</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Leaderboard Name *</label>
                  <select
                    value={newLeaderboard.name}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      let defaultDescription = '';
                      
                      if (selectedName === 'Most Points') {
                        defaultDescription = 'Team with the most points';
                      } else if (selectedName === 'Overall Pinfall') {
                        defaultDescription = 'Overall pinfall across all games';
                      } else if (selectedName === 'Overall Team Pinfall') {
                        defaultDescription = 'Combined team pinfall';
                      }
                      
                      setNewLeaderboard({ name: selectedName, description: defaultDescription });
                      setSelectedDivision('overall');
                    }}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="Most Points" className="bg-gray-800">Most Points</option>
                    <option value="Overall Pinfall" className="bg-gray-800">Overall Pinfall</option>
                    <option value="Overall Team Pinfall" className="bg-gray-800">Overall Team Pinfall</option>
                  </select>
                </div>

                {/* Show division selector only for Overall Pinfall */}
                {newLeaderboard.name === 'Overall Pinfall' && (
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Division *</label>
                    <select
                      value={selectedDivision}
                      onChange={(e) => {
                        const division = e.target.value;
                        setSelectedDivision(division);
                        const divisionText = division === 'overall'
                          ? 'Overall pinfall across all games'
                          : `Division ${division.toUpperCase()}`;
                        setNewLeaderboard({ ...newLeaderboard, description: divisionText });
                      }}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="overall" className="bg-gray-800">Overall</option>
                      <option value="a" className="bg-gray-800">Division A</option>
                      <option value="b" className="bg-gray-800">Division B</option>
                      <option value="c" className="bg-gray-800">Division C</option>
                      <option value="d" className="bg-gray-800">Division D</option>
                      <option value="e" className="bg-gray-800">Division E</option>
                    </select>
                  </div>
                )}

                <div className={newLeaderboard.name === 'Overall Pinfall' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-white/80 mb-2">Description *</label>
                  <input
                    type="text"
                    value={newLeaderboard.description}
                    onChange={(e) => setNewLeaderboard({ ...newLeaderboard, description: e.target.value })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={newLeaderboard.name === 'Overall Pinfall' ? 'Auto-filled based on division' : 'Team with most points wins'}
                    readOnly={newLeaderboard.name === 'Overall Pinfall'}
                  />
                </div>
              </div>
            </div>

            {/* Add Payout Settings */}
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <h4 className="text-lg font-semibold text-white mb-4">2. Add Payout Settings</h4>
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Placement *</label>
                  <input
                    type="number"
                    min="1"
                    value={newLeaderboardPayout.placement}
                    onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, placement: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Type *</label>
                  <select
                    value={newLeaderboardPayout.type}
                    onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, type: e.target.value as 'money' | 'item' })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="money" className="bg-gray-800">Money</option>
                    <option value="item" className="bg-gray-800">Item</option>
                  </select>
                </div>
                {newLeaderboardPayout.type === 'money' ? (
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Amount ($) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newLeaderboardPayout.amount || 0}
                      onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, amount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-white/80 mb-2">Item Description *</label>
                    <input
                      type="text"
                      value={newLeaderboardPayout.item_desc || ''}
                      onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, item_desc: e.target.value })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Trophy, Medal"
                    />
                  </div>
                )}
              </div>
              <button
                onClick={handleAddLeaderboardPayout}
                className="w-full px-4 py-2 bg-blue-600/50 hover:bg-blue-600/70 text-white rounded-lg transition duration-200 border border-blue-400/50"
              >
                Add Complete Leaderboard with Payout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Leaderboards Modal */}
      {showManageLeaderboardsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">⚙️ Manage Leaderboard Payouts</h3>
              <button
                onClick={() => setShowManageLeaderboardsModal(false)}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {payoutSettings.map((setting, index) => (
                <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-semibold text-white">
                      {setting.name === 'Overall Pinfall' && setting.description && setting.description.startsWith('Division ')
                        ? `${setting.name} - ${setting.description}`
                        : setting.name}
                    </h4>
                    <button
                      onClick={() => {
                        setSelectedLeaderboardForManagement(setting);
                        setShowAddLeaderboardPayoutModal(true);
                      }}
                      className="bg-green-600/30 hover:bg-green-600/50 text-green-200 px-3 py-1 rounded-lg transition duration-200 border border-green-400/30 text-sm"
                    >
                      + Add Payout
                    </button>
                  </div>

                  <div className="space-y-2">
                    {setting.payouts.map((payout, pIndex) => (
                      <div key={pIndex} className="flex justify-between items-center bg-white/5 rounded p-2">
                        <div>
                          <span className="text-white font-medium">
                            {payout.placement === 1 ? '1st' : 
                             payout.placement === 2 ? '2nd' : 
                             payout.placement === 3 ? '3rd' : 
                             `${payout.placement}th`} Place
                          </span>
                          <span className="text-green-300 ml-3">
                            {payout.type === 'money' ? `$${payout.amount}` : payout.item_desc}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteLeaderboardPayout(setting, payout.placement)}
                          className="text-red-400 hover:text-red-300 px-2 py-1 rounded transition duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Leaderboard Payout Modal */}
      {showAddLeaderboardPayoutModal && selectedLeaderboardForManagement && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">➕ Add Payout Setting</h3>
              <button
                onClick={() => {
                  setShowAddLeaderboardPayoutModal(false);
                  setSelectedLeaderboardForManagement(null);
                  setNewLeaderboardPayout({ placement: 1, type: 'money', amount: 0, item_desc: '' });
                }}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-white/80 mb-4">
              Adding payout for: <span className="font-bold">
                {selectedLeaderboardForManagement.name}
              </span>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Placement *</label>
                <input
                  type="number"
                  min="1"
                  value={newLeaderboardPayout.placement}
                  onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, placement: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Type *</label>
                <select
                  value={newLeaderboardPayout.type}
                  onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, type: e.target.value as 'money' | 'item' })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="money" className="bg-gray-800">Money</option>
                  <option value="item" className="bg-gray-800">Item</option>
                </select>
              </div>

              {newLeaderboardPayout.type === 'money' ? (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Amount ($) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newLeaderboardPayout.amount || 0}
                    onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Item Description *</label>
                  <input
                    type="text"
                    value={newLeaderboardPayout.item_desc || ''}
                    onChange={(e) => setNewLeaderboardPayout({ ...newLeaderboardPayout, item_desc: e.target.value })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Trophy, Medal"
                  />
                </div>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddLeaderboardPayoutModal(false);
                    setNewLeaderboardPayout({ placement: 1, type: 'money', amount: 0, item_desc: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition duration-200 border border-white/20"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddLeaderboardPayout}
                  className="flex-1 px-4 py-2 bg-blue-600/50 hover:bg-blue-600/70 text-white rounded-lg transition duration-200 border border-blue-400/50"
                >
                  Add Payout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Awards Modal */}
      {showManageAwardsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 border border-white/20 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">⚙️ Manage Awards</h3>
              <button
                onClick={() => setShowManageAwardsModal(false)}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {awards.length === 0 ? (
                <p className="text-white/60 text-center py-8">No awards configured yet. Add one to get started!</p>
              ) : (
                awards.map((award) => (
                  <div key={award.id} className="bg-white/10 rounded-lg p-4 border border-white/10">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-white mb-2">{award.name}</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-white/70">Type:</span>
                            <span className="text-white">
                              {award.type === 'money' ? `$${award.amount} Cash` : award.item_desc}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/70">Awarded:</span>
                            <span className="text-white">
                              {award.per_date && award.overall 
                                ? 'Each Week & Overall'
                                : award.per_date 
                                ? 'Each Week'
                                : 'Overall Only'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteAward(award.id)}
                        className="text-red-400 hover:text-red-300 px-3 py-1 rounded transition duration-200 ml-4"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialComponent;